// import { traceNames, TraceNameType } from '../services/performance.tracking.data'
import { AllSameKeyValuesEnforcer, makeMapFromListOfObjectsAndKey, makeMapFromListOfObjectsAndKeyToProperty } from '../helpers/helpers';

export interface AccessTypeKeys { program; logistics; performer; facilitator; crew; supervisor }
export const PermissionAccessTypes: AllSameKeyValuesEnforcer<AccessTypeKeys> = {
  program:'program', logistics:'logistics', performer:'performer', facilitator:'facilitator', crew:'crew', supervisor:'supervisor'
}

export type AccessType = keyof AccessTypeKeys
const AccessTypes:AccessType[] = ['program', 'logistics', 'performer', 'facilitator', 'crew', 'supervisor']

export type WorkforcePermissionName = 'Program' | 'Logistics' | 'Facilitator' | 'Guest'

export const workforcePermissionNameToType: { [key in WorkforcePermissionName]: AccessType } = { 'Program': 'program', 'Logistics': 'logistics', 'Facilitator': 'facilitator', 'Guest': 'crew' }


export const convertOldPermissions = (permissions): PermissionAccess => {
  if (permissions['types']) return permissions
  return { restrictor: permissions.restrictor, types: AccessTypes.map(k => permissions[k] ? k : null).filter(k => k)}
}

export const getBlankPermissions = (restrictor: string): PermissionAccess => ({ types: [], restrictor })


export interface PermissionAccess {
  types: AccessType[]
  restrictor?:string; // this is who set the permissions. If it's a new event, it is the event creator
}
export interface PageAccessible {
  [key:string]: boolean
}

export const PAGE_NAME = {
  attendees: 'attendees',
  attendees_sessions: 'attendees_sessions',
  attendees_configure: 'attendees_configure',
  cue_sheets: "cue_sheets",
  logistics_build: "logistics_build",
  logistics_views: "logistics_views",
  logistics_share: "logistics_share",
  app: "app",
  share_branding: "share_branding",
  backups: "backups",
  program_table: "program_table",
  submitted_performers: "submitted_performers",
  submitted_sponsors: "submitted_sponsors",
  submitted_exhibitors: "submitted_exhibitors",
  submitted_resources: "submitted_resources",
  performer_questions: "performer_questions",
  exhibitor_questions: "exhibitor_questions",
  resource_questions: "resource_questions",
  asset_table:"asset_table",
  program_dashboard:"program_dashboard",
  program_builder:"program_builder",
  publish:'publish',
  xPublish: 'xPublish',
  performer_management:'performersTable',
  performer_session_management: 'performerSessionsTable',
  performer_information: 'performer_information',
  info_for_performer:'info_for_performer',
  facilitator_notes:'facilitator_notes',
  event_information:'event_information',
  task_table: 'task-table',
  tasks:'task-list',
  task_calendar:'task-calendar',
  taskTime: 'taskTime',
  workforce:'workforce',
  schedules:'schedules',
  private_schedules:'private_schedules',
  program_timeline:'program_timeline',
  performers:'performers',
  oss_timeline:'oss_timeline',
  oss_share: 'oss_share',
  itinerary:'Summary',
  my_schedule:'my_schedule',
  my_tasks_calendar:'my_tasks_calendar',
  my_tasks:'my_tasks',
  my_tasks_table:'my_tasks_table',
  myTaskTime: 'myTaskTime',
  my_sessions:'my_sessions',
  notices:'notices',
  sponsors: 'sponsors',
  sponsor_sessions: 'sponsor_sessions',
  program_summary:'program_summary',
  program_logistics: 'program_logistics',
  exhibitors: 'exhibitors',
  exhibitor_stands: 'exhibitor_stands',
  exhibitor_exhibitor_stands: 'exhibitor_exhibitor_stands',
  exhibitor_information: 'exhibitor_information',
  resources: 'resources',
  resource_information: 'resource_information',
  budget_revenue: 'budget_revenue',
  budget_costs: 'budget_costs',
  budget_quotes: 'budget_quotes',
  budget_admin: 'budget_admin',
  budget_report: 'budget_report'
} as const

export type PageHandle = typeof PAGE_NAME[keyof typeof PAGE_NAME]

export interface JoiPage {
  label:string
  handle: PageHandle
  edit_only_page?:boolean // this means you can't load the page if you only have view
  view_only_page?:boolean // this tells the page panel not to put a locked-eye-con
  is_view_only?:boolean
  in_group?:string
  url:string
  traceName?: any // TraceNameType
}

export type PageGroupHandle = 'event_information' | 'workforce' | 'tasks' | 'my_tasks' | 'program' | 'performers' | 'sponsors' | 'exhibitors' | 'schedules' | 'facilitator_notes' | 'my_schedule' | 'publish' | 'budget' | 'assets' | 'resources' | 'backup' | 'app' | 'logistics' | 'attendees'

export type PageType = PageGroupHandle | 'event dashboard' | 'account dashboard'

export interface PermissionGroup {
  label: string
  handle: AccessType
  desc: string
  selectable_level: boolean
  edit: PageHandle[]
  addRestrictions?: PageHandle[]
  view: PageHandle[]
}



// NOTE: Remember when this is changed to change functions/../system.assets.ts too
export const EVENT_PERMISSIONS: { [K in AccessType]: PermissionGroup } = {
  program:{
    label:'Program Team',
    handle:'program',
    desc:'User can edit all portions of the event',
    selectable_level:true,
    edit:[
      PAGE_NAME.attendees,
      PAGE_NAME.attendees_sessions,
      PAGE_NAME.attendees_configure,
      PAGE_NAME.logistics_build,
      PAGE_NAME.logistics_share,
      PAGE_NAME.logistics_views,
      PAGE_NAME.cue_sheets,
      PAGE_NAME.app,
      PAGE_NAME.share_branding,
      PAGE_NAME.program_table,
      PAGE_NAME.budget_admin,
      PAGE_NAME.budget_costs,
      PAGE_NAME.budget_report,
      PAGE_NAME.budget_revenue,
      PAGE_NAME.budget_quotes,
      PAGE_NAME.resources,
      PAGE_NAME.resource_information,
      PAGE_NAME.program_dashboard,
      PAGE_NAME.program_builder,
      PAGE_NAME.program_logistics,
      PAGE_NAME.program_summary,
      PAGE_NAME.publish,
      PAGE_NAME.performer_management,
      PAGE_NAME.performer_session_management,
      PAGE_NAME.performer_information,
      PAGE_NAME.info_for_performer,
      PAGE_NAME.facilitator_notes,
      PAGE_NAME.event_information,
      PAGE_NAME.task_table,
      PAGE_NAME.tasks,
      PAGE_NAME.task_calendar,
      PAGE_NAME.taskTime,
      PAGE_NAME.workforce,
      PAGE_NAME.schedules,
      PAGE_NAME.private_schedules,
      PAGE_NAME.oss_share,
      PAGE_NAME.notices,
      PAGE_NAME.sponsor_sessions,
      PAGE_NAME.sponsors,
      PAGE_NAME.exhibitor_information,
      PAGE_NAME.exhibitor_stands,
      PAGE_NAME.exhibitor_exhibitor_stands,
      PAGE_NAME.exhibitors,
      PAGE_NAME.asset_table,
      PAGE_NAME.exhibitor_questions,
      PAGE_NAME.performer_questions,
      PAGE_NAME.resource_questions,
      PAGE_NAME.submitted_performers,
      PAGE_NAME.submitted_resources,
      PAGE_NAME.backups
    ],
    view:[
      PAGE_NAME.program_timeline,
      PAGE_NAME.oss_timeline,
      PAGE_NAME.itinerary,
      PAGE_NAME.my_schedule
    ]
  },
  logistics:{
    label: 'Production Team',
    handle: 'logistics',
    desc: 'User can edit only production in the event',
    selectable_level:true,
    edit:[
      PAGE_NAME.attendees,
      PAGE_NAME.attendees_sessions,
      PAGE_NAME.attendees_configure,
      PAGE_NAME.logistics_build,
      PAGE_NAME.logistics_share,
      PAGE_NAME.logistics_views,
      PAGE_NAME.cue_sheets,
      PAGE_NAME.program_table,
      PAGE_NAME.program_builder,
      PAGE_NAME.event_information,
      PAGE_NAME.program_logistics,
      PAGE_NAME.task_table,
      PAGE_NAME.tasks,
      PAGE_NAME.task_calendar,
      PAGE_NAME.taskTime,
      PAGE_NAME.workforce,
      PAGE_NAME.schedules,
      PAGE_NAME.private_schedules,
      PAGE_NAME.notices,
      PAGE_NAME.oss_share,
      PAGE_NAME.exhibitor_information,
      PAGE_NAME.exhibitor_stands,
      PAGE_NAME.exhibitor_exhibitor_stands,
      PAGE_NAME.exhibitors,
      PAGE_NAME.resources,
      PAGE_NAME.resource_information,
      PAGE_NAME.asset_table,
      PAGE_NAME.exhibitor_questions,
      PAGE_NAME.performer_questions,
      PAGE_NAME.resource_questions,
      PAGE_NAME.submitted_performers,
      PAGE_NAME.submitted_resources,
    ],
    view:[
      PAGE_NAME.program_dashboard,
      PAGE_NAME.program_timeline,
      PAGE_NAME.facilitator_notes,
      PAGE_NAME.performers,
      PAGE_NAME.oss_timeline,
      PAGE_NAME.itinerary,
      PAGE_NAME.my_schedule
    ]
  },
  facilitator:{
    label:'Facilitator [MC] Notes',
    handle:'facilitator',
    desc:'Set up for a facilitator',
    selectable_level:true,
    edit:[
      PAGE_NAME.facilitator_notes,
      PAGE_NAME.my_tasks,
      PAGE_NAME.my_tasks_calendar,
      PAGE_NAME.my_tasks_table,
      PAGE_NAME.myTaskTime,
      PAGE_NAME.private_schedules
    ],
    view:[
      PAGE_NAME.program_dashboard,
      PAGE_NAME.program_timeline,
      PAGE_NAME.info_for_performer,
      PAGE_NAME.event_information,
      PAGE_NAME.workforce,
      PAGE_NAME.performers,
      PAGE_NAME.oss_timeline,
      PAGE_NAME.my_schedule,
      PAGE_NAME.notices,
      PAGE_NAME.resources,
    ]
  },
  performer:{
    label:'Performer [speaker]',
    handle:'performer',
    desc:'Set up for a performer',
    selectable_level:false,
    edit:[
      PAGE_NAME.my_sessions,
      PAGE_NAME.my_tasks,
      PAGE_NAME.my_tasks_calendar,
      PAGE_NAME.my_tasks_table,
      PAGE_NAME.myTaskTime,
      PAGE_NAME.private_schedules
    ],
    view:[
      PAGE_NAME.program_dashboard,
      PAGE_NAME.program_timeline,
      PAGE_NAME.info_for_performer,
      PAGE_NAME.event_information,
      PAGE_NAME.workforce,
      PAGE_NAME.my_schedule,
      PAGE_NAME.notices
    ]
  },
  crew:{
    label:'Guest Access',
    handle:'crew',
    desc:'User can view some portions of the event',
    selectable_level:true,
    addRestrictions: [
      PAGE_NAME.my_tasks,
      PAGE_NAME.my_tasks_calendar,
      PAGE_NAME.my_tasks_table,
      PAGE_NAME.myTaskTime,
    ],
    edit:[
      PAGE_NAME.my_tasks,
      PAGE_NAME.my_tasks_calendar,
      PAGE_NAME.my_tasks_table,
      PAGE_NAME.myTaskTime,
    ],
    view:[
      PAGE_NAME.program_dashboard,
      PAGE_NAME.program_timeline,
      PAGE_NAME.event_information,
      PAGE_NAME.workforce,
      PAGE_NAME.private_schedules,
      PAGE_NAME.itinerary,
      PAGE_NAME.my_schedule,
      PAGE_NAME.notices,
    ]
  },
  supervisor:{
    label:'Read Only',
    handle:'supervisor',
    desc:'Auto-assigned to users who are on the account, but not the event workforce',
    selectable_level:false,
    edit:[
    ],
    view:[
      PAGE_NAME.program_dashboard,
      PAGE_NAME.program_builder,
      PAGE_NAME.program_logistics,
      PAGE_NAME.program_summary,
      PAGE_NAME.publish,
      PAGE_NAME.performer_management,
      PAGE_NAME.performer_session_management,
      PAGE_NAME.performer_information,
      PAGE_NAME.info_for_performer,
      PAGE_NAME.facilitator_notes,
      PAGE_NAME.event_information,
      PAGE_NAME.task_table,
      PAGE_NAME.tasks,
      PAGE_NAME.task_calendar,
      PAGE_NAME.taskTime,
      PAGE_NAME.workforce,
      PAGE_NAME.schedules,
      PAGE_NAME.private_schedules,
      PAGE_NAME.oss_share,
      PAGE_NAME.notices,
      PAGE_NAME.sponsor_sessions,
      PAGE_NAME.sponsors,
      PAGE_NAME.program_timeline,
      PAGE_NAME.oss_timeline,
      PAGE_NAME.itinerary,
      PAGE_NAME.exhibitor_information,
      PAGE_NAME.exhibitor_stands,
      PAGE_NAME.exhibitor_exhibitor_stands,
      PAGE_NAME.exhibitors,
      PAGE_NAME.resources,
      PAGE_NAME.resource_information,
    ]
  }
}

export interface PageGroup {
  label:string
  handle: PageGroupHandle
  info:string
  icon:string
  pages_are_editable?:boolean
  pages:JoiPage[]
  mobileHandles?: PageHandle[]
  mobileDefault?: PageHandle
  scale?: number // optional number to scale icon
}

export const BACKUP_GROUP: PageGroup = {
  label:'Backup',
  handle: 'backup',
  info:'Backup or load a backup of your event',
  icon:'OPEN_IN_NEW12',
  pages_are_editable: true,
  mobileHandles: [PAGE_NAME.backups],
  pages:[
    {
      label:'Backups',
      handle:PAGE_NAME.backups,
      url:'event/backups',
      traceName:"View Backups"
    }
  ],
  scale: 0.8
}

export const BUDGET_GROUP: PageGroup = {
  label:'Budget',
  handle:'budget',
  info:'Add a budget for your event',
  icon:'BUDGET48',
  pages_are_editable: true,
  mobileHandles: [PAGE_NAME.budget_report],
  pages:[
    {
      label:'Revenue',
      handle:PAGE_NAME.budget_revenue,
      url:'event/revenue',
      traceName:"View Budget Revenue"
    },
    {
      label:'Costs',
      handle:PAGE_NAME.budget_costs,
      url:'event/costs',
      traceName:"View Budget Revenue"
    },
    {
      label:'Quotes',
      handle:PAGE_NAME.budget_quotes,
      url:'event/budget_quotes',
      traceName:"View Budget Quotes"
    },
    {
      label:'Summary',
      handle:PAGE_NAME.budget_report,
      url:'event/budget_report',
      traceName:"View Budget Report"
    },
    {
      label:'Access',
      handle:PAGE_NAME.budget_admin,
      url:'event/budget_admin',
      traceName:"View Budget Admin"
    }
  ]
}

export const PAGE_INFO: PageGroup[] = [
  {
    label:'Program',
    handle:'program',
    info:'Start your event here. Build the program you would like to share with attendees',
    icon:'programs',
    mobileHandles: ['program_builder', 'program_timeline', 'program_logistics', 'Summary'],
    mobileDefault: 'program_timeline',
    pages:[
      {
        label:'Build',
        handle:PAGE_NAME.program_builder,
        edit_only_page:true, // this means you can't load the page if you only have view
        url:'event/build',
        traceName:"View Program Builder"
      },{
        label:'Room/Locations',
        handle:PAGE_NAME.program_table,
        url:'event/programs',
        traceName:"View Program Table"
      },{
        label:'Sessions',
        handle:PAGE_NAME.performer_session_management,
        url:'event/sessions',
        traceName:"View Sessions"
      },{
        label:'Exhibitor Stands',
        handle:PAGE_NAME.exhibitor_stands,
        url:'event/exhibitor_stands',
        traceName:"View Exhibitor Stands",
      },{
        label:'Preview',
        handle:PAGE_NAME.program_timeline,
        url:'event/timeline',
        traceName:"View Program Timeline"
      }, {
        label:'Summary',
        handle:PAGE_NAME.itinerary,
        url:'event/itinerary',
        traceName:"View Program Summary"
      }
    ]

  }, {
    label:'Event Information',
    handle:'event_information',
    info:'Put in all the information you want to share with your team to deliver the event',
    icon:'INFO48',
    pages:[
      {
        label:'Event Information',
        handle:PAGE_NAME.event_information,
        url:'event/information',
        traceName:"View Event Information"
      }
    ]
  },{
    label:'Workforce',
    handle:'workforce',
    info:'Collaborate with your team and suppliers... and anyone else involved in the organisation of the event',
    icon:'WORKFORCE48',
    pages:[
      {
        label:'Workforce',
        handle:PAGE_NAME.workforce,
        url:'event/workforce',
        traceName:'View Workforce Table'
      }
    ]
  },{
    label:'Tasks',
    handle:'tasks',
    info:'Create and allocate tasks to the team, automated reminders make sure tasks are completed',
    icon:'TASKS48',
    pages:[
      {
        label:'Table',
        handle:PAGE_NAME.task_table,
        url:'event/task-table',
        traceName:"View Tasks Table"
      }, {
        label:'List',
        handle:PAGE_NAME.tasks,
        url:'event/task-list',
        traceName:"View Tasks List"
      }, {
        label:'Calendar',
        handle:PAGE_NAME.task_calendar,
        url:'event/task-calendar',
        traceName:"View Tasks Calendar"
      },{
        label:'Time',
        handle: PAGE_NAME.taskTime,
        url: 'event/task-time',
        traceName: "View Tasks Time"
      }
    ]
  },{
    label:'My Tasks',
    handle:'my_tasks',
    info:'Manage tasks assigned to you',
    icon:'MY_TASKS48',
    pages:[
      {
        label:'Table',
        handle: PAGE_NAME.my_tasks_table,
        url:'event/my_tasks/task-table',
        traceName:"View My Tasks Table"
      },
      {
        label:'List',
        handle: PAGE_NAME.my_tasks,
        url:'event/my_tasks/task-list',
        traceName:"View My Tasks List"
      },{
        label:'Calendar',
        handle: PAGE_NAME.my_tasks_calendar,
        url:'event/my_tasks/task-calendar',
        traceName:"View My Tasks Calendar"
      },{
        label:'Time',
        handle: PAGE_NAME.myTaskTime,
        url: 'event/my_tasks/task-time',
        traceName: "View My Tasks Time"
      }
    ]
  },{
    label:'Resources',
    handle:'resources',
    info:'Manage resources',
    icon:'RESOURCES48',
    pages:[
      {
        label:'Resources',
        handle:PAGE_NAME.resources,
        url:`event/${PAGE_NAME.resources}`,
        traceName:"View Resources",
      },
      {
        label:'Configure',
        handle:PAGE_NAME.resource_information,
        url:`event/${PAGE_NAME.resource_information}`,
        traceName: "View Resource Information"
      },
    ]
  },{
    label:'Event App',
    handle:'app',
    info:'Create apps for your event',
    icon:'MOBILE',
    pages:[
      {
        label:'Event App',
        handle:PAGE_NAME.app,
        url:'event/app',
        traceName:"View Event Apps"
      }
    ]
  },{
    label:'Assets',
    handle:'assets',
    info:'Create and allocate assets across many parts of your event',
    icon:'EVENT_INFORMATION48',
    pages:[
      {
        label:'Assets',
        handle:PAGE_NAME.asset_table,
        url:'event/asset-table',
        traceName:"View Assets Table"
      }
    ]
  },{
    label:'Performers',
    handle:'performers',
    info:'Manage performers and sessions with performers',
    icon:'PERFORMERS48',
    pages:[
      {
        label:'Performers',
        handle:PAGE_NAME.performer_management,
        url:'event/performers',
        traceName:"View Performers"
      },
      {
        label:'Configure',
        handle:PAGE_NAME.performer_information,
        url:'event/performer-information',
        traceName:"View Performer Information"
      },
    ]
  },{
    label:'Sponsors',
    handle:'sponsors',
    info:'Manage your sponsors and the sessions they are on',
    icon:'SPONSOR48',
    pages:[
      {
        label:'Sponsors',
        handle:PAGE_NAME.sponsors,
        url:'event/sponsors',
        traceName:"View Sponsors"
      }
    ]
  },{
    label:'Exhibitors',
    handle:'exhibitors',
    info:'Manage exhibitors',
    icon:'EXHIBITORS_24',
    pages:[
      {
        label:'Exhibitors',
        handle:PAGE_NAME.exhibitors,
        url:'event/exhibitors',
        traceName:"View Exhibitors",
      },{
        label:'Exhibitor Stands',
        handle:PAGE_NAME.exhibitor_exhibitor_stands,
        url:'event/exhibitor_exhibitor_stands',
        traceName:"View Exhibitor Stands",
      },
      {
        label:'Configure',
        handle:PAGE_NAME.exhibitor_information,
        url:'event/exhibitor-information',
        traceName: "View Exhibitor Information"
      },
    ]
  },{
    label:'Attendees',
    handle:'attendees',
    info:'Manage attendees and their sessions',
    icon:'ATTENDEES',
    pages:[
      {
        label:'Attendees',
        handle:PAGE_NAME.attendees,
        url:'event/attendees',
        traceName:"View Attendees"
      },{
        label:'Sessions',
        handle:PAGE_NAME.attendees_sessions,
        url:'event/attendees-sessions',
        traceName:"View Attendee Sessions"
      },
      {
        label:'Configure',
        handle:PAGE_NAME.attendees_configure,
        url:'event/attendees-configure',
        traceName:"View Attendee Configure"
      },
    ]
  },{
    label:'Production',
    handle:'logistics',
    info:'Manage production',
    icon:'LOGISTICS',
    mobileHandles: [PAGE_NAME.logistics_views, PAGE_NAME.logistics_share, PAGE_NAME.cue_sheets, PAGE_NAME.facilitator_notes],
    pages:[
      {
        label:'Build',
        handle:PAGE_NAME.logistics_build,
        url:'event/production-build',
        traceName:"View Logistics Build",
      },{
        label:'Views',
        handle:PAGE_NAME.logistics_views,
        url:'event/production-views',
        traceName:"View Logistics Views",
      }, {
        label:'Share',
        handle:PAGE_NAME.logistics_share,
        url:'event/production-share',
        traceName: "View Logistics Share"
      }, {
        label:'Run of Show',
        handle:PAGE_NAME.cue_sheets,
        url:'event/cue-sheets',
        traceName:"View Cue Sheets"
      }, {
        label:'Facilitator Notes',
        handle:PAGE_NAME.facilitator_notes,
        url:'event/facilitator',
        traceName:"View Facilitator Notes"
      }
    ]
  },{
    label:'On Site Schedules',
    handle:'schedules',
    info:'Enter all the activities to set up, run and pack down your event... then view on mobile',
    icon:'ON_SITE_SCHEDULE48',
    mobileHandles: [PAGE_NAME.schedules, PAGE_NAME.oss_timeline, PAGE_NAME.oss_share],
    mobileDefault: PAGE_NAME.oss_timeline,
    pages:[
      {
        label:'Build',
        handle:PAGE_NAME.schedules,
        url:'event/onsite/build',
        traceName:"View On Site Schedule Build"
      }, {
        label:'Timeline',
        handle:PAGE_NAME.oss_timeline,
        url:'event/onsite/timeline',
        traceName:"View On Site Schedule Timeline",
      }, {
        label:'Share',
        handle:PAGE_NAME.oss_share,
        url:'event/onsite/share',
        traceName:"View On Site Schedule Share"
      }
    ]
  }, {
    label:'My Schedule',
    handle:'my_schedule',
    info:'Activities and sessions that you’re tagged in',
    icon:'MY_SCHEDULE48',
    pages:[
      {
        label:'My Schedule',
        handle:PAGE_NAME.my_schedule,
        view_only_page:true, // this tells the page panel not to put a locked=
        url:'event/onsite/my_schedule',
        traceName:"View My Schedule"
      }
    ]
  },{
    label:'Publish',
    handle:'publish',
    info:'Publish events',
    icon:'PUBLISH',
    mobileHandles: [PAGE_NAME.publish],
    pages:[
      {
        label:'Webpages',
        handle:PAGE_NAME.publish,
        url:'event/publish',
        // traceName: traceNames.viewProgramPublish
      }, {
        label:'Forms',
        handle:PAGE_NAME.share_branding,
        url:'event/share_branding',
        // traceName: traceNames.viewShareBranding
      }
    ]
  }, BUDGET_GROUP, BACKUP_GROUP
] 

export const pageGroupHandles: { [K in PageGroupHandle]: K }  = makeMapFromListOfObjectsAndKeyToProperty(PAGE_INFO, 'handle', 'handle') as any
export const pageGroupsByHandles: { [K in PageGroupHandle]: PageGroup }  = makeMapFromListOfObjectsAndKey(PAGE_INFO, 'handle') as any

export const pageGroupHandleBySubpageHandle:{[K in PageHandle]: PageGroupHandle} = {} as any
PAGE_INFO.forEach(group => group.pages.forEach(page => {
  pageGroupHandleBySubpageHandle[page.handle] = group.handle
}))


export const pageUrlsToPage = {}
PAGE_INFO.forEach(p => p.pages.forEach(page => pageUrlsToPage[page.url] = page))